/**
 * batch-59 voertuig-afspraken (FO-435 + FO-436) - gedeelde types, Zod-schema's
 * en DTO's.
 *
 * Eén request-schema per mutatie, gedeeld door de API (`safeParse` ->
 * `sendValidationError`) en het formulier (client-side dezelfde `safeParse` vóór
 * verzenden). Zo kan het formulier niets sturen dat de API weigert, en landen
 * echte invoerfouten inline op het veld (CLAUDE.md - Validatie & foutmeldingen).
 */

import { z } from 'zod'
import type { VoertuigOnderhoudType } from './voertuigen'
// batch-62 garage-voertuig (FO-465) - alleen TYPES (import type = geen runtime-
// cyclus; garage-voertuig.ts importeert de schema's uit dit bestand).
import type {
  VoertuigAfspraakDocumentDTO,
  VoertuigAfspraakFactuurDTO,
} from './garage-voertuig'

// ─── Enums (spiegel van schema.prisma) ───────────────────────────────────────

// batch-67 middelen-inname (FO-505 §12.3): REPARATIE = de afspraak bij een reparatieverzoek.
export const VOERTUIG_AFSPRAAK_SOORT = ['ONDERHOUD', 'SCHADE', 'APK', 'REPARATIE'] as const
export type VoertuigAfspraakSoort = (typeof VOERTUIG_AFSPRAAK_SOORT)[number]

export const VOERTUIG_AFSPRAAK_SOORT_LABELS: Record<VoertuigAfspraakSoort, string> = {
  ONDERHOUD: 'Onderhoud',
  SCHADE: 'Schade',
  APK: 'APK',
  REPARATIE: 'Reparatie',
}

export const VOERTUIG_AFSPRAAK_STATUS = [
  'TE_PLANNEN',
  'GEPLAND',
  'UITGEVOERD',
  'GEANNULEERD',
  'NIET_VERSCHENEN',
] as const
export type VoertuigAfspraakStatus = (typeof VOERTUIG_AFSPRAAK_STATUS)[number]

export const VOERTUIG_AFSPRAAK_STATUS_LABELS: Record<VoertuigAfspraakStatus, string> = {
  TE_PLANNEN: 'Te plannen',
  GEPLAND: 'Gepland',
  UITGEVOERD: 'Uitgevoerd',
  GEANNULEERD: 'Geannuleerd',
  NIET_VERSCHENEN: 'Niet verschenen',
}

/** Statussen waarin een afspraak nog "loopt" (werkvoorraad). */
export const VOERTUIG_AFSPRAAK_OPEN_STATUSSEN: readonly VoertuigAfspraakStatus[] = [
  'TE_PLANNEN',
  'GEPLAND',
]

/**
 * batch-62 garage-voertuig (FO-465 §A) - één registratie, drie ingangen: het
 * voertuigdossier, het planbord (dagregel voor de berijder die wegbrengt) en een
 * operationele taak; plus de reminder "volgende actie" als vierde herkomst.
 */
export const VOERTUIG_AFSPRAAK_INGANG = ['VOERTUIGDOSSIER', 'PLANBORD', 'TAAK', 'REMINDER'] as const
export type VoertuigAfspraakIngang = (typeof VOERTUIG_AFSPRAAK_INGANG)[number]

export const VOERTUIG_AFSPRAAK_INGANG_LABELS: Record<VoertuigAfspraakIngang, string> = {
  VOERTUIGDOSSIER: 'Voertuigdossier',
  PLANBORD: 'Planbord',
  TAAK: 'Operationele taak',
  REMINDER: 'Reminder volgende actie',
}

export const VOERTUIG_TERUGROEP_INTERNE_STATUS = [
  'OPEN',
  'INGEPLAND',
  'UITGEVOERD',
  'NIET_VAN_TOEPASSING',
] as const
export type VoertuigTerugroepInterneStatus = (typeof VOERTUIG_TERUGROEP_INTERNE_STATUS)[number]

export const VOERTUIG_TERUGROEP_INTERNE_STATUS_LABELS: Record<
  VoertuigTerugroepInterneStatus,
  string
> = {
  OPEN: 'Open',
  INGEPLAND: 'Ingepland',
  UITGEVOERD: 'Uitgevoerd',
  NIET_VAN_TOEPASSING: 'Niet van toepassing',
}

/**
 * De interne statussen die als werkvoorraad tellen. FO-436 §5: het filter
 * "open recalls" telt UITSLUITEND deze, zodat een intern afgehandelde recall
 * nergens meer als werk verschijnt - ook al blijft RDW op "O" staan.
 */
export const TERUGROEP_INTERN_OPEN_STATUSSEN: readonly VoertuigTerugroepInterneStatus[] = [
  'OPEN',
  'INGEPLAND',
]

export const VOERTUIG_HISTORIE_IMPORT_STATUS = ['CONCEPT', 'VERWERKT', 'GEANNULEERD'] as const
export type VoertuigHistorieImportStatus = (typeof VOERTUIG_HISTORIE_IMPORT_STATUS)[number]

export const VOERTUIG_HISTORIE_REGEL_STATUS = [
  'NIEUW',
  'DUPLICAAT',
  'MOGELIJK_DUPLICAAT',
  'ONMATCHBAAR',
  'GEREGISTREERD',
  'OVERGESLAGEN',
] as const
export type VoertuigHistorieRegelStatus = (typeof VOERTUIG_HISTORIE_REGEL_STATUS)[number]

export const VOERTUIG_HISTORIE_REGEL_STATUS_LABELS: Record<VoertuigHistorieRegelStatus, string> = {
  NIEUW: 'Nieuw',
  DUPLICAAT: 'Duplicaat',
  MOGELIJK_DUPLICAAT: 'Mogelijk duplicaat',
  ONMATCHBAAR: 'Onmatchbaar',
  GEREGISTREERD: 'Geregistreerd',
  OVERGESLAGEN: 'Overgeslagen',
}

// ─── Gedeelde validatie-bouwstenen ───────────────────────────────────────────

/** yyyy-mm-dd (de UI stuurt altijd de kalenderdag, nooit een tijdstip). */
const isoDagSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik een geldige datum (dd-mm-jjjj)')

/** "HH:mm" (24-uurs, Europe/Amsterdam). */
const tijdSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Gebruik een tijd in de vorm uu:mm')

/**
 * batch-62 garage-voertuig (FO-465 §A.2) - de reden is VERPLICHT bij het
 * aanmaken (soort + korte omschrijving); hij staat meteen als eerste regel in
 * de voertuighistorie met status "gepland". Gedeeld door alle drie de ingangen.
 */
export const afspraakRedenSchema = z
  .string({ message: 'Geef de reden van de afspraak op' })
  .trim()
  .min(3, 'Geef de reden van de afspraak op (bijv. "APK vervalt 12-10" of "ruit gebarsten")')
  .max(2000, 'Maximaal 2000 tekens')

// ─── Afspraak: aanmaken / bijwerken ──────────────────────────────────────────

export const createVoertuigAfspraakSchema = z
  .object({
    soort: z.enum(VOERTUIG_AFSPRAAK_SOORT),
    /** Leeg = TE_PLANNEN ("actie gewenst, nog geen datum"). */
    afspraakDatum: isoDagSchema.nullable().optional(),
    afspraakTijd: tijdSchema.nullable().optional(),
    garageClientId: z.string().min(1).nullable().optional(),
    garageNaam: z.string().trim().max(200).nullable().optional(),
    /** batch-62 (FO-465 §A.2) - de reden: verplicht bij aanmaken. */
    omschrijving: afspraakRedenSchema,
    /** FO-436 §6 - de recall waarvoor deze afspraak wordt gepland. */
    terugroepReferentie: z.string().trim().max(50).nullable().optional(),
    /**
     * batch-62 (FO-465 §A) - de ingang. Het planbord heeft een eigen endpoint;
     * via dit schema komen het dossier (default), een taak en een reminder.
     */
    ingang: z.enum(['VOERTUIGDOSSIER', 'TAAK', 'REMINDER']).optional(),
    /** De operationele taak waaruit de afspraak voortkomt (ingang TAAK). */
    bronTaakId: z.string().min(1).nullable().optional(),
    /** De reminder (volgende actie) die deze afspraak dekt (ingang REMINDER). */
    reminderId: z.string().min(1).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.afspraakTijd && !v.afspraakDatum) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['afspraakTijd'],
        message: 'Vul eerst een datum in voordat je een tijd kiest.',
      })
    }
  })
export type CreateVoertuigAfspraakInput = z.infer<typeof createVoertuigAfspraakSchema>

export const updateVoertuigAfspraakSchema = z
  .object({
    soort: z.enum(VOERTUIG_AFSPRAAK_SOORT).optional(),
    afspraakDatum: isoDagSchema.nullable().optional(),
    afspraakTijd: tijdSchema.nullable().optional(),
    garageClientId: z.string().min(1).nullable().optional(),
    garageNaam: z.string().trim().max(200).nullable().optional(),
    // batch-62 (FO-465 §A.2) - de reden mag bijgesteld worden, niet leeggemaakt.
    omschrijving: afspraakRedenSchema.optional(),
    terugroepReferentie: z.string().trim().max(50).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.afspraakTijd && v.afspraakDatum === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['afspraakTijd'],
        message: 'Vul eerst een datum in voordat je een tijd kiest.',
      })
    }
  })
export type UpdateVoertuigAfspraakInput = z.infer<typeof updateVoertuigAfspraakSchema>

/**
 * Status-overgangen die de UI kan aanvragen. UITGEVOERD loopt NIET via dit
 * endpoint maar via `afrondenVoertuigAfspraakSchema` (die eist datum + logregel).
 */
export const VOERTUIG_AFSPRAAK_STATUS_ACTIE = [
  'GEPLAND',
  'GEANNULEERD',
  'NIET_VERSCHENEN',
  'HEROPENEN',
] as const
export type VoertuigAfspraakStatusActie = (typeof VOERTUIG_AFSPRAAK_STATUS_ACTIE)[number]

export const voertuigAfspraakStatusSchema = z
  .object({
    actie: z.enum(VOERTUIG_AFSPRAAK_STATUS_ACTIE),
    /** Verplicht bij GEANNULEERD en NIET_VERSCHENEN. */
    reden: z.string().trim().max(1000).nullable().optional(),
    /** Alleen bij GEPLAND: de (nieuwe) afspraakdatum. */
    afspraakDatum: isoDagSchema.nullable().optional(),
    afspraakTijd: tijdSchema.nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if ((v.actie === 'GEANNULEERD' || v.actie === 'NIET_VERSCHENEN') && !v.reden?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reden'],
        message: 'Geef een reden op.',
      })
    }
    if (v.actie === 'GEPLAND' && !v.afspraakDatum) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['afspraakDatum'],
        message: 'Een geplande afspraak heeft een datum nodig.',
      })
    }
  })
export type VoertuigAfspraakStatusInput = z.infer<typeof voertuigAfspraakStatusSchema>

/**
 * Afronden: zet de afspraak op UITGEVOERD en maakt (of koppelt) de
 * onderhoudslogregel. Datum/km/soort worden voorgevuld door de UI, maar mogen
 * hier bijgesteld worden.
 */
export const afrondenVoertuigAfspraakSchema = z
  .object({
    uitvoerDatum: isoDagSchema,
    kilometerstand: z.number().int().min(0).max(2_000_000).nullable().optional(),
    garage: z.string().trim().max(200).nullable().optional(),
    beschrijving: z.string().trim().max(2000).nullable().optional(),
    /** Koppelen aan een bestaande logregel i.p.v. een nieuwe maken. */
    onderhoudId: z.string().min(1).nullable().optional(),
    /**
     * FO-436 §6 - zet de gekoppelde recall in dezelfde handeling op UITGEVOERD.
     * Bewust een expliciete keuze (één klik), nooit automatisch.
     */
    recallOpUitgevoerd: z.boolean().optional(),
    /**
     * batch-62 (FO-465 §B.4) - al binnengekomen inkoopfacturen die bij deze
     * afspraak horen. Ze worden via de garagefactuur-automatiek aan de
     * logregel van deze afspraak gekoppeld (twee facturen = bedragen tellen op).
     */
    inkoopFactuurIds: z.array(z.string().min(1)).max(10).optional(),
    /** batch-62 (FO-465 §5.6) - afronden zonder factuur (garantie/coulance): bedrag 0. */
    geenFactuur: z.boolean().optional(),
    geenFactuurReden: z.string().trim().max(1000).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.geenFactuur && (v.geenFactuurReden?.trim().length ?? 0) < 3) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['geenFactuurReden'],
        message: 'Geef aan waarom er geen factuur komt (bijv. garantie of coulance).',
      })
    }
    if (v.geenFactuur && (v.inkoopFactuurIds?.length ?? 0) > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['geenFactuur'],
        message: 'Koppel een factuur of kies "geen factuur", niet allebei.',
      })
    }
  })
export type AfrondenVoertuigAfspraakInput = z.infer<typeof afrondenVoertuigAfspraakSchema>

/** "Zet in planning" - interne werkbon/dagregel voor het wegbrengen/ophalen. */
export const voertuigAfspraakPlanningSchema = z.object({
  employeeId: z.string().min(1, 'Kies een medewerker'),
  startTijd: tijdSchema.default('08:00'),
  eindTijd: tijdSchema.default('10:00'),
  /** Doorzetten ondanks planning-conflicten (dubbele inplanning/ziekte/training). */
  force: z.boolean().optional(),
})
export type VoertuigAfspraakPlanningInput = z.infer<typeof voertuigAfspraakPlanningSchema>

// ─── Terugroepactie: interne afhandeling ─────────────────────────────────────

export const TERUGROEP_AFHANDEL_ACTIE = [
  'UITGEVOERD',
  'NIET_VAN_TOEPASSING',
  'HEROPENEN',
  // FO-436 §5 - beheer wijst een berijder-voorstel af (met reden); de interne
  // status blijft open, de voorgestelde datum/garage worden gewist.
  'VOORSTEL_AFWIJZEN',
] as const
export type TerugroepAfhandelActie = (typeof TERUGROEP_AFHANDEL_ACTIE)[number]

/**
 * FO-436 §6.1 - UITGEVOERD vereist een datum (niet in de toekomst) én
 * garage/toelichting; NIET_VAN_TOEPASSING vereist een reden. HEROPENEN zet de
 * recall terug op OPEN (bevestiging van een RDW-heropening).
 */
export const terugroepAfhandelingSchema = z
  .object({
    actie: z.enum(TERUGROEP_AFHANDEL_ACTIE),
    uitgevoerdOp: isoDagSchema.nullable().optional(),
    uitgevoerdGarage: z.string().trim().max(200).nullable().optional(),
    notitie: z.string().trim().max(2000).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.actie === 'UITGEVOERD') {
      if (!v.uitgevoerdOp) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['uitgevoerdOp'],
          message: 'Vul de datum in waarop de actie is uitgevoerd.',
        })
      }
      if (!v.uitgevoerdGarage?.trim() && !v.notitie?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['uitgevoerdGarage'],
          message: 'Vul de garage in, of licht toe wie het heeft uitgevoerd.',
        })
      }
    }
    if (v.actie === 'NIET_VAN_TOEPASSING' && !v.notitie?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['notitie'],
        message: 'Geef een reden op waarom deze actie niet van toepassing is.',
      })
    }
    if (v.actie === 'VOORSTEL_AFWIJZEN' && !v.notitie?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['notitie'],
        message: 'Geef aan waarom het voorstel wordt afgewezen.',
      })
    }
  })
export type TerugroepAfhandelingInput = z.infer<typeof terugroepAfhandelingSchema>

/**
 * Berijder-voorstel (FO-436 §5): de bestuurder MELDT dat het is uitgevoerd,
 * beheer bevestigt. Geen zelf-wegklikken van een veiligheidssignaal.
 */
export const terugroepVoorstelSchema = z.object({
  uitgevoerdOp: isoDagSchema,
  uitgevoerdGarage: z.string().trim().max(200).nullable().optional(),
  notitie: z.string().trim().max(2000).nullable().optional(),
})
export type TerugroepVoorstelInput = z.infer<typeof terugroepVoorstelSchema>

// ─── Historie-import ─────────────────────────────────────────────────────────

export const historieImportRegelSchema = z.object({
  positie: z.number().int().min(0),
  kentekenRuw: z.string().max(50),
  kenteken: z.string().max(20),
  datum: isoDagSchema.nullable(),
  omschrijving: z.string().max(2000).nullable(),
  /** Decimale string ("1234.56"); nooit een float - centen mogen niet zwerven. */
  bedrag: z
    .string()
    .regex(/^-?\d+(\.\d{1,2})?$/, 'Ongeldig bedrag')
    .nullable(),
  kmStand: z.number().int().min(0).max(2_000_000).nullable(),
})
export type HistorieImportRegelInput = z.infer<typeof historieImportRegelSchema>

export const createHistorieImportSchema = z
  .object({
    garageClientId: z.string().min(1).nullable().optional(),
    garageNaam: z.string().trim().min(1, 'Kies of typ de garage').max(200),
    bronOmschrijving: z.string().trim().max(200).nullable().optional(),
    regels: z
      .array(historieImportRegelSchema)
      .min(1, 'Plak minstens één historie-regel')
      .max(5000, 'Maximaal 5000 regels per aanlevering'),
  })
  .strict()
export type CreateHistorieImportInput = z.infer<typeof createHistorieImportSchema>

export const verwerkHistorieImportSchema = z.object({
  /**
   * Dry-run: analyseer + rapporteer, schrijf géén logregels. Default true zodat
   * een vergeten vlag nooit stil boekt.
   */
  dryRun: z.boolean().default(true),
  /**
   * Twijfelgevallen (MOGELIJK_DUPLICAAT) die de behandelaar TOCH wil boeken.
   * NIEUW-regels gaan altijd mee; een niet-genoemd twijfelgeval blijft op de
   * review-lijst staan (nooit stil overslaan).
   */
  posities: z.array(z.number().int().min(0)).max(5000).optional(),
})
export type VerwerkHistorieImportInput = z.infer<typeof verwerkHistorieImportSchema>

// ─── Instellingen (FO-435 §10 A4 - drempels zijn instelbaar) ─────────────────

export interface VoertuigAfspraakInstellingen {
  /** Verval binnen zoveel dagen zonder afspraak -> "Actie vereist" (APK). */
  apkDrempelDagen: number
  /** Idem voor de verzekerings-vervaldatum. */
  verzekeringDrempelDagen: number
  /** Matching-venster (dagen vóór/ná de werkdatum) voor de factuur-koppeling. */
  factuurVensterDagen: number
  /** Na hoeveel dagen een onbevestigd berijder-voorstel beheer herinnert. */
  voorstelHerinneringDagen: number
}

export const VOERTUIG_AFSPRAAK_INSTELLINGEN_DEFAULTS: VoertuigAfspraakInstellingen = {
  apkDrempelDagen: 30,
  verzekeringDrempelDagen: 30,
  factuurVensterDagen: 7,
  voorstelHerinneringDagen: 5,
}

export const voertuigAfspraakInstellingenSchema = z.object({
  apkDrempelDagen: z.number().int().min(1).max(365),
  verzekeringDrempelDagen: z.number().int().min(1).max(365),
  factuurVensterDagen: z.number().int().min(0).max(60),
  voorstelHerinneringDagen: z.number().int().min(1).max(60),
})

// ─── DTO's ───────────────────────────────────────────────────────────────────

export interface VoertuigAfspraakDTO {
  id: string
  voertuigId: string
  kenteken: string
  soort: VoertuigAfspraakSoort
  status: VoertuigAfspraakStatus
  /** ISO-dag of null. */
  afspraakDatum: string | null
  afspraakTijd: string | null
  garageClientId: string | null
  garageNaam: string | null
  omschrijving: string | null
  terugroepReferentie: string | null
  uitgevoerdOp: string | null
  onderhoudId: string | null
  werkbonId: string | null
  werkbonNummer: string | null
  planEmployeeId: string | null
  planEmployeeNaam: string | null
  reden: string | null
  aangemaaktDoorNaam: string | null
  afgerondDoorNaam: string | null
  /**
   * FO-435 stap 6 - de kosten van deze afspraak: de som van de voertuig-
   * aandelen op de facturen die aan de gekoppelde logregel hangen. De factuur
   * blijft de waarheid; er is bewust geen eigen bedragveld op de afspraak.
   */
  kosten: number | null
  // ── batch-62 garage-voertuig (FO-465 §A/§B) ─────────────────────────────
  /** Waar de afspraak is aangemaakt (null = vóór batch-62). */
  ingang: VoertuigAfspraakIngang | null
  bronTaakId: string | null
  wegbrengTaakId: string | null
  /** Afgerond zonder factuur (garantie/coulance) met deze reden; bedrag 0. */
  geenFactuurReden: string | null
  /** Facturen op de keten (via de logregel); PDF via de inkoop-proxy. */
  facturen: VoertuigAfspraakFactuurDTO[]
  /** Factuur/pakbon/werkorder-documenten; PDF via de documenten-proxy. */
  documenten: VoertuigAfspraakDocumentDTO[]
  createdAt: string
}

/** Eén item uit `voertuigActieStatus` in DTO-vorm (server-side gerekend). */
export interface VoertuigActieStatusItemDTO {
  // batch-62 (FO-465 §8) - ONDERHOUD = de reminder "volgende actie" (beurt/banden).
  soort: 'APK' | 'VERZEKERING' | 'TERUGROEP' | 'ONDERHOUD'
  niveau: 'GEEN' | 'UITGEVOERD' | 'GEPLAND' | 'ACTIE_VEREIST'
  vervaldatum: string | null
  dagenTotVerloop: number | null
  afspraakId: string | null
  afspraakDatum: string | null
  wachtOpDatum: boolean
  /** Waarom een bestaande geplande afspraak dit signaal niet dekt. */
  afspraakProbleem: 'VERSTREKEN' | 'NA_VERVALDATUM' | null
  /** Kant-en-klare kopij ("Actie vereist", "Gepland 12-04-2026"). */
  label: string
}

export interface VoertuigActieStatusDTO {
  niveau: 'GEEN' | 'UITGEVOERD' | 'GEPLAND' | 'ACTIE_VEREIST'
  actieVereist: boolean
  items: VoertuigActieStatusItemDTO[]
}

/** De interne afhandel-kant van een recall (FO-436). */
export interface VoertuigTerugroepAfhandelingDTO {
  interneStatus: VoertuigTerugroepInterneStatus
  uitgevoerdOp: string | null
  uitgevoerdGarage: string | null
  afhandelNotitie: string | null
  afgehandeldDoorNaam: string | null
  afgehandeldOp: string | null
  voorstelDoorNaam: string | null
  voorstelOp: string | null
  voorstelNotitie: string | null
  afspraakId: string | null
  /** RDW meldt 'm weer open ná een interne afhandeling: bevestiging gevraagd. */
  heropendOp: string | null
  heropenBevestigdOp: string | null
  /**
   * Kant-en-klare dossierregel: "RDW: openstaand - intern: uitgevoerd op
   * {datum}". Server-side samengesteld zodat lijst en detail identiek lezen.
   */
  gecombineerdeStatus: string
}

/**
 * FO-436 §7 - garagefactuur-hint. Een onderhoudsregel uit een werkplaatsfactuur
 * waarvan de omschrijving de recall-referentie bevat: "mogelijk uitgevoerd op
 * {factuurdatum} - registreren?". Afgeleid bij het lezen (geen extra state).
 */
export interface VoertuigTerugroepFactuurSuggestieDTO {
  onderhoudId: string
  /** ISO-dag van de onderhoudsregel (= werkdatum van de factuur). */
  datum: string
  garage: string | null
  omschrijving: string | null
}

/** Recall + RDW-inhoud + de interne afhandel-kant, zoals het dossier 'm toont. */
export interface VoertuigTerugroepDossierDTO {
  id: string
  voertuigId: string
  referentiecodeRdw: string
  openstaand: boolean
  statusOmschrijving: string
  omschrijvingDefect: string | null
  categorieDefect: string | null
  risicobeoordeling: string | null
  herstelOmschrijving: string | null
  meldendeProducent: string | null
  publicatiedatumRdw: string | null
  laatstGezienOp: string
  afhandeling: VoertuigTerugroepAfhandelingDTO
  /** Mogelijke garagefactuur-treffer op de referentiecode (max 1, nieuwste). */
  factuurSuggestie: VoertuigTerugroepFactuurSuggestieDTO | null
}

export interface VoertuigHistorieImportRegelDTO {
  id: string
  positie: number
  kentekenRuw: string
  kenteken: string
  datum: string | null
  omschrijving: string | null
  bedrag: number | null
  kmStand: number | null
  type: VoertuigOnderhoudType | null
  status: VoertuigHistorieRegelStatus
  reden: string | null
  voertuigId: string | null
  voertuigKenteken: string | null
  onderhoudId: string | null
}

export interface VoertuigHistorieImportDTO {
  id: string
  garageClientId: string | null
  garageNaam: string
  bronOmschrijving: string | null
  status: VoertuigHistorieImportStatus
  aangemaaktDoorNaam: string | null
  verwerktDoorNaam: string | null
  verwerktOp: string | null
  createdAt: string
  /** Telling per regel-status (rapport). */
  telling: Record<VoertuigHistorieRegelStatus, number>
  aantalRegels: number
}

export interface VoertuigHistorieImportDetailDTO extends VoertuigHistorieImportDTO {
  regels: VoertuigHistorieImportRegelDTO[]
}

/** Resultaat van de dry-run/verwerking (rapport voor het scherm + STATUS). */
export interface VoertuigHistorieImportRapportDTO {
  importId: string
  dryRun: boolean
  telling: Record<VoertuigHistorieRegelStatus, number>
  /** Aantal daadwerkelijk aangemaakte onderhoudslogregels (0 bij dry-run). */
  geregistreerd: number
  regels: VoertuigHistorieImportRegelDTO[]
}
